import axios from 'axios';
import toast from 'react-hot-toast';

// Compute base URLs with safe fallbacks and runtime overrides
const isDev = process.env.NODE_ENV === 'development';
const strictAuth = (process.env.REACT_APP_STRICT_AUTH === 'true') && !isDev;

// mme_api_base is a developer/ops config override — intentionally kept in localStorage
// so it survives tab closes and can be set once per machine. It is NOT auth data.
const runtimeBase = (typeof window !== 'undefined') ? (localStorage.getItem('mme_api_base') || '') : '';

// Default cloud bases (unused in localhost when proxy is active)
const defaultTaggingBase = 'https://api.railtech.io';
const defaultTagmakerBase = 'https://tagmaker.railtech.io';

// If app is served from a known gateway host (nip.io or railtech), prefer same host
const currentHost = (typeof window !== 'undefined') ? window.location.hostname : '';
// Only treat nip.io domains as gateway hosts (where the frontend is served
// from the same host as the API). Custom domains like mme.railtech.io are
// Vercel-hosted frontends that must proxy API calls to the backend.
const isGatewayHost = currentHost.endsWith('.nip.io');
const sameHostBase = isGatewayHost ? `https://${currentHost}` : '';

// For local development, use CRA proxy-relative paths to avoid CORS
const isLocalDev = currentHost === 'localhost' || currentHost === '127.0.0.1';
const localTaggingBase = isLocalDev ? '/api/tagging' : '';
const localTagmakerBase = isLocalDev ? '/api/tagmaker' : '';

// Resolve final bases with dev-friendly behavior:
// - On localhost, use proxy-relative paths
// - Elsewhere, allow env to override
const envTaggingBase = process.env.REACT_APP_TAGGING_API_URL || '';
const envTagmakerBaseRaw = process.env.REACT_APP_TAGMAKER_API_URL || '';

// Guard against common misconfig: tagmaker pointing at mme host
const envTagmakerBase = envTagmakerBaseRaw.includes('mme.localhost')
  ? envTagmakerBaseRaw.replace('mme.localhost', 'tagmaker.localhost')
  : envTagmakerBaseRaw;

const preferProxy = isLocalDev && process.env.REACT_APP_USE_PROXY !== 'false';

const resolvedTaggingBase = preferProxy
  ? (localTaggingBase || '/api/tagging')
  : (envTaggingBase || sameHostBase || defaultTaggingBase);

const resolvedTagmakerBase = preferProxy
  ? (localTagmakerBase || '/api/tagmaker')
  : (envTagmakerBase || sameHostBase || defaultTagmakerBase);

// Create axios instance for Tagging Service
const taggingApi = axios.create({
  baseURL: resolvedTaggingBase,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Disable credentials for CORS
});

// Create axios instance for Tagmaker Service
const tagmakerApi = axios.create({
  baseURL: resolvedTagmakerBase,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Disable credentials for CORS
});

// API configuration logged in dev only — avoids leaking base URLs in production console
if (isDev) {
  console.log('API Configuration:', {
    taggingApi: taggingApi.defaults.baseURL || 'relative (proxied)',
    tagmakerApi: tagmakerApi.defaults.baseURL || 'relative (proxied)',
    env: process.env.NODE_ENV,
    currentHost,
  });
}

// Request interceptor to add auth headers.
// Auth data is read from sessionStorage — tokens stored there are tab-scoped and
// do not persist across browser restarts, limiting the XSS theft window.
const addAuthHeaders = (config) => {
  const token = sessionStorage.getItem('mme_token');
  const userId = sessionStorage.getItem('mme_user_id') || 'test_user_123';
  const orgId = sessionStorage.getItem('mme_org_id') || 'test-org';
  const shouldAttachAuth = !!token && token !== 'demo-token' && token.length > 20;

  // ----- 1. Authorization header (required for Traefik ForwardAuth in production) -----
  if (shouldAttachAuth) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }

  // ----- 2. Identity headers (X-User-ID, X-Org-ID, X-User-Roles) -----
  // In production, Traefik ForwardAuth injects these from the JWT.  We set them
  // here as well so that (a) local dev without Traefik still works and (b) the
  // tagging service receives them regardless of Traefik configuration.
  if (shouldAttachAuth) {
    try {
      const parts = token.split('.');
      const payload = JSON.parse(atob(parts[1]));
      config.headers['X-User-ID'] = payload.userId || payload.sub || userId;
      config.headers['X-Org-ID'] = payload.org_id || payload.orgId || orgId;
      if (payload.role || payload.roles) {
        const role = payload.role || (Array.isArray(payload.roles) ? payload.roles[0] : 'user');
        config.headers['X-User-Roles'] = JSON.stringify([role]);
      }
    } catch {
      // JWT parse failed — fall back to sessionStorage values
      config.headers['X-User-ID'] = userId;
      config.headers['X-Org-ID'] = orgId;
    }
  } else if (isLocalDev) {
    // Dev convenience — no JWT, use sessionStorage identifiers
    config.headers['X-User-ID'] = userId;
    config.headers['X-Org-ID'] = orgId;
    const role = sessionStorage.getItem('mme_user_role') || 'user';
    config.headers['X-User-Roles'] = JSON.stringify([role]);
  }

  // ----- 3. Auto-inject orgId into POST/PUT bodies (must match X-Org-ID for tenancy) -----
  if (config.method && ['post', 'put', 'patch'].includes(config.method.toLowerCase())) {
    if (config.data && typeof config.data === 'object') {
      const needsOrgId = [
        '/memory/inject', '/memory/save', '/memory/promote',
        '/tags/extract', '/tags/query', '/tags/delta',
        '/search/semantic', '/events/pack',
        '/processing/backfill-tags', '/admin/cleanup', '/admin/edge/prune', '/admin/features',
        '/extract-tags', '/generate-and-save', '/manual-rebalance', '/admin/edge-learn/replay'
      ];
      const needsOrgIdEndpoint = needsOrgId.some(ep => config.url?.includes(ep));
      if (needsOrgIdEndpoint) {
        if (shouldAttachAuth) {
          // Use org from JWT (must match X-Org-ID header for ValidateTenancy)
          config.data.orgId = config.headers['X-Org-ID'] || orgId;
        } else {
          // No auth — remove orgId to avoid tenancy 403 in dev
          delete config.data.orgId;
        }
      }
    }
  }

  return config;
};

taggingApi.interceptors.request.use(addAuthHeaders, (error) => Promise.reject(error));
tagmakerApi.interceptors.request.use(addAuthHeaders, (error) => Promise.reject(error));

// Response interceptor for error handling
const handleResponse = (response) => response;
let hasRedirectedFor401 = false;

// Known endpoints that may not be available in development/demo mode
const knownOptionalEndpoints = [
  '/metrics',
  '/security/health',
  '/security/metrics',
  '/version',
  '/database-debug',
  '/database-direct-test',
  '/admin/stats',
  '/admin/platform-stats',
  '/admin/users'
];

const handleError = async (error) => {
  const status = error.response?.status;
  const url = error.config?.url || '';
  
  if (status === 401) {
    if (strictAuth) {
      // Clear auth and redirect only in strict mode (production)
      sessionStorage.removeItem('mme_token');
      sessionStorage.removeItem('mme_user_id');
      if (!hasRedirectedFor401 && window.location.pathname !== '/login') {
        hasRedirectedFor401 = true;
        window.location.href = '/login';
      }
    } else {
      // Non-strict/dev: surface error but do not force logout
      console.debug('401 received (non-strict): staying on page');
    }
  }
  
  // Handle 404s gracefully for known optional endpoints
  if (status === 404) {
    const isOptionalEndpoint = knownOptionalEndpoints.some(endpoint => url.includes(endpoint));
    if (isOptionalEndpoint) {
      // Don't show toast errors or log to console for known optional endpoints
      // The individual functions will handle this gracefully with fallback data
      return Promise.reject(error);
    }
  }
  
  // For other errors, show user-friendly messages
  if (status >= 400) {
    const message = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'An error occurred';
    // Only show toast for non-404 or non-optional endpoints
    if (!(status === 404 && knownOptionalEndpoints.some(endpoint => url.includes(endpoint)))) {
      toast.error(message);
    }
  }

  return Promise.reject(error);
};

taggingApi.interceptors.response.use(handleResponse, handleError);
tagmakerApi.interceptors.response.use(handleResponse, handleError);

// ===== TAGGING SERVICE ENDPOINTS =====

// System endpoints
export const getTaggingHealth = async () => {
  const response = await taggingApi.get('/health');
  const data = response.data;
  // Normalize to consistent shape
  if (typeof data === 'string') return { status: data };
  return data;
};

export const getTaggingVersion = async () => {
  const response = await taggingApi.get('/version');
  return response.data;
};

export const getTaggingStatus = async () => {
  const response = await taggingApi.get('/');
  return response.data;
};

export const getTaggingMetrics = async () => {
  const response = await taggingApi.get('/metrics');
  return response.data;
};

// Memory Management (9 endpoints)
export const saveMemory = async (memoryData) => {
  const response = await taggingApi.post('/memory/save', memoryData);
  return response.data;
};

export const queryMemories = async (params) => {
  const response = await taggingApi.get('/memory/query', { params });
  return response.data;
};

export const queryMemoriesDebug = async (params) => {
  const response = await taggingApi.get('/memory/query-debug', { params });
  return response.data;
};

export const getRecentMemories = async (params) => {
  const response = await taggingApi.get('/memory/recent', { params });
  return response.data;
};

// Returns the real DB total memory count for the requesting (orgID, userID),
// not the page size of /memory/recent. Used by dashboard KPIs so the
// "Total memories" number reflects reality even when corpus > fetch limit.
export const getMemoryCount = async () => {
  const response = await taggingApi.get('/memory/count');
  return response.data;
};

// Aggregated memory stats for the dashboard:
//   { totalMemories, totalTokens, memoriesLast30d, memoriesLast7d,
//     byDay: [{date, count, tokens}], firstSavedAt, lastSavedAt }
// Single Mongo aggregation server-side; fast even for large corpora.
export const getMemoryStats = async () => {
  const response = await taggingApi.get('/memory/stats');
  return response.data;
};

export const deleteMemory = async (id) => {
  const response = await taggingApi.delete(`/memory/${id}`);
  return response.data;
};

export const promoteMemories = async (promoteData) => {
  const response = await taggingApi.post('/memory/promote', promoteData);
  return response.data;
};

export const injectMemory = async (memoryData) => {
  const response = await taggingApi.post('/memory/inject', memoryData);
  return response.data;
};

export const getTokenizerHealth = async () => {
  const response = await taggingApi.get('/memory/tokenizer-health');
  return response.data;
};

// Tag Operations (3 endpoints)
export const extractTags = async (extractData) => {
  // Normalize payload to tagging service contract: { prompt }
  const prompt = extractData?.prompt || extractData?.content || '';
  const payload = { prompt };
  
  try {
    const response = await taggingApi.post('/tags/extract', payload);
    return response.data;
  } catch (err) {
    // Fallback path for local/dev when tagging endpoint rejects due to tenancy/auth
    const status = err?.response?.status;
    if ((status === 401 || status === 403) && prompt) {
      try {
        const fallbackBody = { content: prompt, userId: extractData?.userId || 'test_user_123' };
        const fb = await tagmakerApi.post('/extract-tags', fallbackBody);
        const tags = fb?.data?.tags || fb?.data?.data?.tags || [];
        return { data: { tags } };
      } catch (fbErr) {
        throw err; // surface original error if fallback fails
      }
    }
    throw err;
  }
};

export const queryByPrompt = async (promptData) => {
  const response = await taggingApi.post('/tags/query', promptData);
  return response.data;
};

export const updateTagDelta = async (deltaData) => {
  const response = await taggingApi.post('/tags/delta', deltaData);
  return response.data;
};

export const getAllTags = async () => {
  const response = await taggingApi.get('/tags/all');
  return response.data;
};

// Search Operations (1 endpoint)
export const semanticSearch = async (searchData) => {
  const response = await taggingApi.post('/search/semantic', searchData);
  return response.data;
};

// Events Processing (1 endpoint)
// Pack accept-rate aggregation. Returns
//   { orgId, windowHours, total, accepted, rejected, acceptRate }
// over the requested window (default 30d). Powers the home dashboard's
// Pack acceptance KPI without leaking individual events.
export const getPackAggregate = async (params) => {
  const response = await taggingApi.get('/events/pack/aggregate', { params });
  return response.data;
};

export const handlePackEvent = async (eventData) => {
  const response = await taggingApi.post('/events/pack', eventData);
  return response.data;
};

// Background Processing (1 endpoint)
export const backfillTags = async (backfillData) => {
  const response = await taggingApi.post('/processing/backfill-tags', backfillData);
  return response.data;
};

// Admin Operations (5 endpoints)
export const getAdminStats = async () => {
  const response = await taggingApi.get('/admin/stats');
  return response.data;
};

// Live process metrics for admin SLO panel — uptime, memory, RPM, goroutines.
// Distinct from the prom-style /metrics endpoint (which is global counters).
export const getAdminMetrics = async () => {
  const response = await taggingApi.get('/admin/metrics');
  return response.data;
};

// IDF stats per-org: corpus size, smoothing alpha, gate threshold, plus the
// top-N tags by docCount with their computed IDF values. Used by the admin
// "Tag economics" panel to surface what the retrieval layer treats as noise
// vs. signal — useful for tuning tag-cleaning rules and seeing IDF in action.
export const getAdminIDFStats = async (params) => {
  const response = await taggingApi.get('/admin/idf-stats', { params });
  return response.data;
};

// Founder-only platform stats (subscriber count, signup histogram, etc.).
// Gated server-side by JWT + admin role + founder email allowlist — regular
// users and non-founder admins receive 403.
export const getPlatformStats = async () => {
  const response = await taggingApi.get('/admin/platform-stats');
  return response.data;
};

// Founder-only user list. Returns the full roster of account holders with
// email, signup date, auth provider, and last active. Same 3-layer auth.
export const getUserList = async () => {
  const response = await taggingApi.get('/admin/users');
  return response.data;
};

export const cleanupMemories = async (cleanupData) => {
  const response = await taggingApi.post('/admin/cleanup', cleanupData);
  return response.data;
};

export const pruneEdges = async (pruneData) => {
  const response = await taggingApi.post('/admin/edge/prune', pruneData);
  return response.data;
};

export const getAdminFeatures = async (orgId) => {
  const params = {};
  // Prefer explicit orgId param, otherwise fall back to stored org
  const finalOrgId = orgId || (typeof window !== 'undefined' ? sessionStorage.getItem('mme_org_id') : undefined);
  if (finalOrgId) params.orgId = finalOrgId;
  const response = await taggingApi.get('/admin/features', { params });
  return response.data;
};

export const setAdminFeatures = async (featuresData) => {
  const response = await taggingApi.post('/admin/features', featuresData);
  return response.data;
};

// Security endpoints (moved to tagmaker service)
// These endpoints are now handled by getTagmakerSecurityHealth and getTagmakerSecurityMetrics

// ===== TAGMAKER SERVICE ENDPOINTS =====

// System endpoints
export const getTagmakerHealth = async () => {
  const response = await tagmakerApi.get('/health');
  const data = response.data;
  // Ensure shape
  return typeof data === 'object' ? data : { status: String(data || '') };
};

export const getTagmakerMetrics = async () => {
  try {
    const response = await tagmakerApi.get('/metrics');
    return response.data;
  } catch (err) {
    // Endpoint not available, return mock data indicating unavailability
    if (err?.response?.status === 404) {
      console.debug('Metrics endpoint not available (404)');
      return { 
        status: 'endpoint_unavailable', 
        message: 'Metrics endpoint not implemented on this server',
        timestamp: new Date().toISOString()
      };
    }
    throw err; // Re-throw other errors
  }
};

export const getTagmakerDocs = async () => {
  const response = await tagmakerApi.get('/docs');
  return response.data;
};

export const getTagmakerReDoc = async () => {
  const response = await tagmakerApi.get('/redoc');
  return response.data;
};

export const getTagmakerOpenAPI = async () => {
  const response = await tagmakerApi.get('/openapi.json');
  return response.data;
};

// Business endpoints (7 endpoints)
export const getTagmakerStatus = async () => {
  const response = await tagmakerApi.get('/');
  return response.data;
};

export const getQueueStatus = async () => {
  const response = await tagmakerApi.get('/queue-status');
  const data = response.data || {};
  return { status: 'ok', ...data };
};

export const getDatabaseStatus = async () => {
  const response = await tagmakerApi.get('/database-status');
  const data = response.data || {};
  const healthy = data.database_connected === true || data.status === 'ok';
  return { status: healthy ? 'ok' : 'unhealthy', ...data };
};


export const manualRebalance = async (rebalanceData) => {
  const response = await tagmakerApi.post('/manual-rebalance', rebalanceData);
  return response.data;
};

export const extractTagsFromContent = async (extractData) => {
  const response = await tagmakerApi.post('/extract-tags', extractData);
  return response.data;
};

export const generateAndSave = async (generateData) => {
  const response = await tagmakerApi.post('/generate-and-save', generateData);
  return response.data;
};

// Tagmaker Security endpoints
export const getTagmakerSecurityHealth = async () => {
  try {
    const response = await tagmakerApi.get('/security/health');
    return response.data;
  } catch (err) {
    // Endpoint not available, return mock data
    console.log('Security health endpoint not available (404)');
    return { 
      status: 'endpoint_unavailable', 
      message: 'Security health endpoint not implemented on this server',
      timestamp: new Date().toISOString()
    };
  }
};

export const getTagmakerSecurityMetrics = async () => {
  try {
    const response = await tagmakerApi.get('/security/metrics');
    return response.data;
  } catch (err) {
    // Endpoint not available, return mock data
    console.log('Security metrics endpoint not available (404)');
    return { 
      status: 'endpoint_unavailable', 
      message: 'Security metrics endpoint not implemented on this server',
      timestamp: new Date().toISOString()
    };
  }
};

export const getTagmakerVersion = async () => {
  try {
    const response = await tagmakerApi.get('/version');
    return response.data;
  } catch (err) {
    // Endpoint not available, return mock data
    console.log('Version endpoint not available (404)');
    return { 
      status: 'endpoint_unavailable', 
      message: 'Version endpoint not implemented on this server',
      timestamp: new Date().toISOString()
    };
  }
};

export const getDatabaseDebug = async () => {
  try {
    const response = await tagmakerApi.get('/database-debug');
    return response.data;
  } catch (err) {
    // Endpoint not available, return mock data
    console.log('Database debug endpoint not available (404)');
    return { 
      status: 'endpoint_unavailable', 
      message: 'Database debug endpoint not implemented on this server',
      timestamp: new Date().toISOString()
    };
  }
};

export const testDatabaseDirect = async () => {
  try {
    const response = await tagmakerApi.get('/database-direct-test');
    return response.data;
  } catch (err) {
    // Endpoint not available, return mock data
    console.log('Database direct test endpoint not available (404)');
    return { 
      status: 'endpoint_unavailable', 
      message: 'Database direct test endpoint not implemented on this server',
      timestamp: new Date().toISOString()
    };
  }
};

// Tagmaker Admin endpoints
export const triggerEdgeLearning = async (learningData) => {
  const response = await tagmakerApi.post('/admin/edge-learn/replay', learningData);
  return response.data;
};

// Tagmaker Utility endpoints
export const submitFeedback = async (feedbackData) => {
  try {
    const response = await tagmakerApi.post('/feedback', feedbackData);
    return response.data;
  } catch (err) {
    console.log('Feedback endpoint not available (404) - returning mock data');
    return {
      status: 'endpoint_unavailable',
      message: 'Feedback service not available. Backend service may be down.',
      mockData: true,
      feedbackId: `mock_feedback_${Date.now()}`,
      submitted: true,
      feedback: feedbackData.feedback,
      acknowledgment: 'Thank you for your feedback! (This is a mock response since the service is unavailable)',
      timestamp: new Date().toISOString()
    };
  }
};


export const getTagmakerRootStatus = async () => {
  const response = await tagmakerApi.get('/');
  return response.data;
};

// Tagmaker Admin queue ops
export const clearFailedQueue = async () => {
  const response = await tagmakerApi.get('/admin/queue-clear');
  return response.data;
};

export const retryFailedQueue = async () => {
  const response = await tagmakerApi.get('/admin/queue-retry');
  return response.data;
};

export const forceRebalance = async () => {
  const response = await tagmakerApi.post('/admin/force-rebalance');
  return response.data;
};

// ===== MONITORING ENDPOINTS =====

export const extractTagsOnly = async (extractData) => {
  try {
    const response = await tagmakerApi.post('/extract-tags', extractData);
    return response.data;
  } catch (err) {
    console.log('Extract tags endpoint not available (404) - returning mock data');
    return {
      status: 'endpoint_unavailable',
      message: 'Extract tags service not available. Backend service may be down.',
      mockData: true,
      tags: [
        { tag: 'completion', confidence: 0.95, category: 'status' },
        { tag: 'submission', confidence: 0.92, category: 'action' },
        { tag: 'IRAP', confidence: 0.98, category: 'process' },
        { tag: 'documents', confidence: 0.89, category: 'content' },
        { tag: 'budget_approval', confidence: 0.93, category: 'outcome' }
      ],
      extractedInfo: {
        subject: 'IRAP submission process',
        action: 'completed successfully',
        outcome: 'approved',
        sentiment: 'positive'
      },
      timestamp: new Date().toISOString(),
      processingTime: '0.45s (mock)'
    };
  }
};

export const generateAndSaveTags = async (generateData) => {
  try {
    const response = await tagmakerApi.post('/generate-and-save', generateData);
    return response.data;
  } catch (err) {
    console.log('Generate and save tags endpoint not available (404) - returning mock data');
    return {
      status: 'endpoint_unavailable',
      message: 'Generate and save service not available. Backend service may be down.',
      mockData: true,
      success: true,
      memoryBlockId: `mock_block_${Date.now()}`,
      tags: [
        { tag: 'completion', confidence: 0.95, category: 'status' },
        { tag: 'submission', confidence: 0.92, category: 'action' },
        { tag: 'IRAP', confidence: 0.98, category: 'process' },
        { tag: 'documents', confidence: 0.89, category: 'content' },
        { tag: 'budget_approval', confidence: 0.93, category: 'outcome' }
      ],
      savedTo: {
        userId: generateData.userId,
        orgId: generateData.orgId,
        section: 'ai_generated'
      },
      tokenUsage: {
        prompt: 156,
        completion: 89,
        total: 245
      },
      timestamp: new Date().toISOString(),
      processingTime: '0.67s (mock)'
    };
  }
};

export const triggerManualRebalance = async () => {
  try {
    const response = await tagmakerApi.post('/manual-rebalance');
    return response.data;
  } catch (err) {
    console.log('Manual rebalance endpoint not available (404) - returning mock data');
    return {
      status: 'endpoint_unavailable',
      message: 'Manual rebalance service not available. Backend service may be down.',
      mockData: true,
      rebalanceId: `mock_rebalance_${Date.now()}`,
      action: 'triggered',
      estimatedTime: '2-5 minutes',
      blocksToProcess: 1247,
      priority: 'normal',
      timestamp: new Date().toISOString()
    };
  }
};

export const triggerEdgeLearningReplay = async (learningData) => {
  try {
    const response = await tagmakerApi.post('/admin/edge-learn/replay', learningData);
    return response.data;
  } catch (err) {
    console.log('Edge learning replay endpoint not available (404) - returning mock data');
    return {
      status: 'endpoint_unavailable',
      message: 'Edge learning replay service not available. Backend service may be down.',
      mockData: true,
      replayId: `mock_replay_${Date.now()}`,
      orgId: learningData.orgId,
      timeRange: `${learningData.hours || 24} hours`,
      action: 'triggered',
      estimatedPatterns: 89,
      processingQueue: 'pending',
      timestamp: new Date().toISOString()
    };
  }
};

// ===== AUTHENTICATION =====

export const login = async (credentials) => {
  // Frontend-only login helper: prefer a provided JWT or existing stored token.
  const provided = credentials?.jwtToken;
  const existing = (typeof window !== 'undefined') ? sessionStorage.getItem('mme_token') : '';
  const envDefault = process.env.REACT_APP_DEFAULT_JWT;

  const candidate = provided || existing || envDefault || '';
  return Promise.resolve({ token: candidate, user: credentials });
};

export const logout = async () => {
  // For demo purposes, simulate successful logout
  return Promise.resolve({ success: true });
};

// Health check with authentication context
export const authenticatedHealthCheck = async () => {
  try {
    // Try tagging service first (main service)
    const taggingHealth = await getTaggingHealth();
    
    // Also check tagmaker if available
    const tagmakerHealth = await getTagmakerHealth();
    
    return {
      status: 'healthy',
      services: {
        tagging: taggingHealth,
        tagmaker: tagmakerHealth
      },
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Health check failed:', error);
    throw error;
  }
};

// Validate current authentication status
export const validateAuth = async () => {
  try {
    const token = sessionStorage.getItem('mme_token');
    if (!token) {
      throw new Error('No authentication token');
    }

    // Test with a simple health check
    await getTaggingHealth();

    return {
      valid: true,
      token: token,
      user: {
        id: sessionStorage.getItem('mme_user_id'),
        name: sessionStorage.getItem('mme_user_name'),
        email: sessionStorage.getItem('mme_user_email'),
        role: sessionStorage.getItem('mme_user_role'),
        orgId: sessionStorage.getItem('mme_org_id')
      }
    };
  } catch (error) {
    return {
      valid: false,
      error: error.message
    };
  }
};

// ===== API SERVICE OBJECT =====

// Add missing getSystemHealth function
export const getSystemHealth = async () => {
  try {
    // Try to get both service healths and aggregate them
    const [taggingHealth, tagmakerHealth] = await Promise.allSettled([
      getTaggingHealth(),
      getTagmakerHealth()
    ]);
    
    const overall = {
      status: 'healthy',
      services: {
        tagging: taggingHealth.status === 'fulfilled' ? taggingHealth.value : { status: 'unhealthy', error: taggingHealth.reason?.message },
        tagmaker: tagmakerHealth.status === 'fulfilled' ? tagmakerHealth.value : { status: 'unhealthy', error: tagmakerHealth.reason?.message }
      },
      timestamp: new Date().toISOString()
    };
    
    // Determine overall status
    if (taggingHealth.status === 'rejected' && tagmakerHealth.status === 'rejected') {
      overall.status = 'unhealthy';
    } else if (taggingHealth.status === 'rejected' || tagmakerHealth.status === 'rejected') {
      overall.status = 'degraded';
    }
    
    return overall;
  } catch (error) {
    return {
      status: 'unhealthy',
      error: error.message,
      timestamp: new Date().toISOString()
    };
  }
};

export const apiService = {
  // Diagnostics
  getApiConfig: () => ({
    taggingBase: taggingApi.defaults.baseURL,
    tagmakerBase: tagmakerApi.defaults.baseURL,
    currentHost,
    strictAuth,
    env: process.env.NODE_ENV || (isDev ? 'development' : 'production'),
  }),
  getSystemHealth,
  // Tagging Service
  getTaggingHealth,
  getTaggingVersion,
  getTaggingStatus,
  getTaggingMetrics,
  saveMemory,
  queryMemories,
  queryMemoriesDebug,
  getRecentMemories,
  getMemoryCount,
  getMemoryStats,
  deleteMemory,
  promoteMemories,
  injectMemory,
  getTokenizerHealth,
  extractTags,
  queryByPrompt,
  updateTagDelta,
  getAllTags,
  semanticSearch,
  handlePackEvent,
  getPackAggregate,
  backfillTags,
  getAdminStats,
  getAdminMetrics,
  getAdminIDFStats,
  getPlatformStats,
  getUserList,
  cleanupMemories,
  pruneEdges,
  getAdminFeatures,
  setAdminFeatures,
  
  // Tagmaker Service
  getTagmakerHealth,
  getTagmakerMetrics,
  getTagmakerDocs,
  getTagmakerReDoc,
  getTagmakerOpenAPI,
  getTagmakerStatus,
  getQueueStatus,
  getDatabaseStatus,
  getDatabaseDebug,
  testDatabaseDirect,
  manualRebalance,
  extractTagsFromContent,
  generateAndSave,
  getTagmakerSecurityHealth,
  getTagmakerSecurityMetrics,
  triggerEdgeLearning,
  submitFeedback,
  getTagmakerVersion,
  clearFailedQueue,
  retryFailedQueue,
  forceRebalance,
  
  // Monitoring Endpoints
  extractTagsOnly,
  generateAndSaveTags,
  triggerManualRebalance,
  triggerEdgeLearningReplay,
  
  // Authentication
  login,
  logout,

  // Signup — public, no auth required
  // In dev: proxy via /api/auth which rewrites to jwt-verifier
  // On gateway host: same origin (Traefik routes /auth/* to jwt-verifier)
  // On Vercel/other: use the resolved tagging base (GCP backend)
  signup: async (email) => {
    const base = isLocalDev ? '/api' : (isGatewayHost ? '' : resolvedTaggingBase);
    const response = await axios.post(`${base}/auth/signup`, { email }, { timeout: 10000 });
    return response.data;
  },

  // Exchange API key for JWT
  exchangeApiKey: async (apiKey) => {
    const base = isLocalDev ? '/api' : (isGatewayHost ? '' : resolvedTaggingBase);
    const response = await axios.post(`${base}/auth/exchange`, { apiKey }, { timeout: 10000 });
    return response.data;
  },

  // Exchange a Google Identity Services ID token for an MME JWT. New users
  // receive apiKey: null — they generate it explicitly via createApiKey().
  // Returning users who already have a key get it here for backwards compat.
  // Returns { token, user_id, org_id, email, name, picture, apiKey, has_api_key, is_new_user }.
  loginWithGoogle: async (credential) => {
    const base = isLocalDev ? '/api' : (isGatewayHost ? '' : resolvedTaggingBase);
    const response = await axios.post(
      `${base}/auth/google`,
      { credential },
      { timeout: 10000 }
    );
    return response.data;
  },

  // GET the current user's API key. These calls use raw axios (not the
  // taggingApi/tagmakerApi instances), so the auth interceptor does NOT
  // run — we attach the MME JWT from sessionStorage manually here. 404
  // means no key has been created yet — the dashboard renders the
  // "Create API Key" button.
  getCurrentApiKey: async () => {
    const base = isLocalDev ? '/api' : (isGatewayHost ? '' : resolvedTaggingBase);
    const token = sessionStorage.getItem('mme_token') || '';
    const response = await axios.get(`${base}/auth/api-keys/current`, {
      timeout: 10000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  // POST to generate a new API key for the current user. Idempotent by
  // default (returns existing key with created=false if one exists).
  // Pass { force: true } to rotate — the previous key stops working.
  createApiKey: async ({ force = false } = {}) => {
    const base = isLocalDev ? '/api' : (isGatewayHost ? '' : resolvedTaggingBase);
    const token = sessionStorage.getItem('mme_token') || '';
    const response = await axios.post(
      `${base}/auth/api-keys/create`,
      force ? { force: true } : {},
      {
        timeout: 10000,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  // Legacy compatibility
  getHealth: getTaggingHealth,
  querySearch: semanticSearch,
  queryBlocksDebug: queryMemoriesDebug,
  getSystemInfo: getAdminStats,
};

export default apiService;
